import { useState } from "react";

import { useCurrentWorkspace } from "@accurx/auth";
import { Ds, SearchSelect } from "@accurx/design";
import { SkeletonLoader } from "@accurx/inbox-design-library";
import { useContactServicesQuery } from "domains/directory/hooks/queries/useContactServicesQuery";
import styled from "styled-components";

type ContactServiceSelectorProps = {
    name: string;
    isInvalid?: boolean;
    selectedServiceId?: number;
};

export const ContactServiceSelector = ({
    name,
    isInvalid,
    selectedServiceId: defaultServiceId,
}: ContactServiceSelectorProps) => {
    const { orgId: workspaceId } = useCurrentWorkspace();
    const { data, isLoading, isError } = useContactServicesQuery({
        workspaceId,
    });

    const [selectedServiceId, setSelectedServiceId] = useState(
        defaultServiceId?.toString() ?? "",
    );

    const handleChange: React.ComponentProps<
        typeof SearchSelect
    >["onChangeHandler"] = (selected) => {
        const serviceId = Array.isArray(selected)
            ? selected[0].value
            : selected.value;
        setSelectedServiceId(serviceId);
    };

    return (
        <>
            {isLoading && <SkeletonLoader role="status" height="40px" />}
            {isError && <Ds.Text>Could not load services</Ds.Text>}
            {data && (
                <>
                    <SearchSelect
                        zIndex={2}
                        options={data.services.map((service) => ({
                            label: `${service.name ?? service.id}`,
                            value: `${service.id}`,
                        }))}
                        id={name}
                        onChangeHandler={handleChange}
                        initSelectedValues={
                            selectedServiceId
                                ? {
                                      label: `${
                                          data.services.find(
                                              (service) =>
                                                  service.id.toString() ===
                                                  selectedServiceId,
                                          )?.name ?? selectedServiceId
                                      }`,
                                      value: `${selectedServiceId}`,
                                  }
                                : undefined
                        }
                        defaultCopy={{ placeholder: "Select service" }}
                    />
                    <HiddenSelect
                        id={name}
                        name={name}
                        value={selectedServiceId}
                        onChange={() => {
                            return;
                        }}
                    >
                        <option value="">Select a service</option>
                        {data.services.map((service) => (
                            <option key={service.id} value={service.id}>
                                {service.name}
                            </option>
                        ))}
                    </HiddenSelect>
                </>
            )}
        </>
    );
};

const HiddenSelect = styled.select`
    display: none;
`;
